import { Head } from '@inertiajs/react'
import { useMemo, useState } from 'react'
import {
  Bell,
  CalendarHeart,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Coffee,
  Download,
  Footprints,
  Gem,
  Heart,
  Mail,
  Medal,
  Megaphone,
  PartyPopper,
  Rocket,
  Sparkles,
  Ticket,
  Users,
  Wine,
  type LucideIcon,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'

type Milestone = {
  key: string
  title: string
  offsetLabel: string
  description: string
  suggestions: string[]
  icon: LucideIcon
  computeDate: (eventDate: Date) => Date
}

type EventTypePlan = {
  key: string
  label: string
  milestones: Milestone[]
}

const subtractMonths = (date: Date, months: number) => {
  const d = new Date(date)
  d.setMonth(d.getMonth() - months)
  return d
}

const subtractWeeks = (date: Date, weeks: number) => {
  const d = new Date(date)
  d.setDate(d.getDate() - weeks * 7)
  return d
}

const subtractDays = (date: Date, days: number) => {
  const d = new Date(date)
  d.setDate(d.getDate() - days)
  return d
}

const GALA_MILESTONES: Milestone[] = [
  {
    key: 'save_the_date',
    title: 'Save the Date',
    offsetLabel: '6 months before',
    description:
      'Lock in attention early. Send a brief, beautifully branded note so guests can hold the date on their calendars.',
    suggestions: [
      'Send a digital Save the Date with the event date and city',
      'Tease the cause or honoree without giving everything away',
      'Add the event to your website with a placeholder hero',
      'Begin building your guest list and confirming honorees',
    ],
    icon: CalendarHeart,
    computeDate: (d) => subtractMonths(d, 6),
  },
  {
    key: 'formal_invitation',
    title: 'Formal Invitation',
    offsetLabel: '10 weeks before',
    description:
      'The official invitation goes out. This is when guests learn the full program, the dress code, and how to RSVP.',
    suggestions: [
      'Mail printed invitations or send a polished digital invite',
      'Open ticket sales and sponsorship packages',
      'Confirm honorees, speakers, and entertainment',
      'Publish the event landing page with the full program',
    ],
    icon: Mail,
    computeDate: (d) => subtractWeeks(d, 10),
  },
  {
    key: 'announcement_1',
    title: 'Announcements & Engagement (1)',
    offsetLabel: '8 weeks before',
    description:
      'First wave of public engagement. Spotlight the cause and start building social momentum.',
    suggestions: [
      'Announce the event publicly across social channels',
      'Share the story behind the cause and the impact you fund',
      'Send a press release to local media partners',
      'Start an email drip campaign to past attendees',
    ],
    icon: Megaphone,
    computeDate: (d) => subtractWeeks(d, 8),
  },
  {
    key: 'announcement_2',
    title: 'Announcements & Engagement (2)',
    offsetLabel: '6 weeks before',
    description:
      'Second engagement wave. Reveal the honorees, headliners, and notable guests.',
    suggestions: [
      'Profile each honoree with a short story and image',
      'Tease the live and silent auction highlights',
      'Highlight sponsors and host committee members',
      'Send a reminder to early-bird ticket buyers',
    ],
    icon: Sparkles,
    computeDate: (d) => subtractWeeks(d, 6),
  },
  {
    key: 'announcement_3',
    title: 'Announcements & Engagement (3)',
    offsetLabel: '4 weeks before',
    description:
      'Final push for ticket sales. Drive urgency and remove every last reason not to attend.',
    suggestions: [
      'Send last-call ticket emails with a clear deadline',
      'Publish a final program preview with menu and timing',
      'Run a targeted ad campaign to your warm audiences',
      'Personally reach out to top prospects who have not RSVP\'d',
    ],
    icon: Megaphone,
    computeDate: (d) => subtractWeeks(d, 4),
  },
  {
    key: 'event_logistics',
    title: 'Event Logistics',
    offsetLabel: '1 week before',
    description:
      'Finalize every operational detail. Walk through the run of show with the venue and vendors.',
    suggestions: [
      'Confirm final headcount with the venue and caterer',
      'Share the run of show with the team and AV crew',
      'Print name badges, seating charts, and auction paddles',
      'Send guests parking, dress code, and arrival reminders',
    ],
    icon: ClipboardList,
    computeDate: (d) => subtractWeeks(d, 1),
  },
  {
    key: 'day_of',
    title: 'Day of Event',
    offsetLabel: 'Day of event',
    description:
      'It is showtime. Lead with calm, delegate everything that can be delegated, and enjoy the night.',
    suggestions: [
      'Arrive early to walk the room with your venue lead',
      'Brief volunteers and check-in staff on the run of show',
      'Capture photos and quotes for the post-event recap',
      'Send a heartfelt thank-you message to guests within 24 hours',
    ],
    icon: PartyPopper,
    computeDate: (d) => new Date(d),
  },
]

const RACE_MILESTONES: Milestone[] = [
  {
    key: 'registration_opens',
    title: 'Registration Opens',
    offsetLabel: '5 months before',
    description:
      'Open the floodgates early. A long registration runway gives participants time to commit, train, and start raising money.',
    suggestions: [
      'Launch online registration with early-bird and team pricing tiers',
      'Publish the route map, distance options, and start time',
      'Set a clear fundraising goal and share why it matters',
      'Add the race to your website and local event calendars',
    ],
    icon: Ticket,
    computeDate: (d) => subtractMonths(d, 5),
  },
  {
    key: 'team_fundraising_launch',
    title: 'Team & Fundraising Launch',
    offsetLabel: '3 months before',
    description:
      'Turn individual sign-ups into teams. Peer-to-peer fundraising is where charity races raise the most.',
    suggestions: [
      'Recruit team captains and give them a quick-start toolkit',
      'Open personal and team fundraising pages',
      'Announce fundraising incentives and milestone rewards',
      'Share early-bird success stories to build momentum',
    ],
    icon: Users,
    computeDate: (d) => subtractMonths(d, 3),
  },
  {
    key: 'sponsor_vendor_push',
    title: 'Sponsor & Vendor Push',
    offsetLabel: '8 weeks before',
    description:
      'Lock in the partners who fund the day. Sponsors and expo vendors offset costs and add energy to the start line.',
    suggestions: [
      'Confirm presenting and tier sponsors with logo placements',
      'Sign up expo, food, and activation booth vendors',
      'Secure in-kind donations like water, snacks, and prizes',
      'Order branded signage, banners, and finish-line assets',
    ],
    icon: Sparkles,
    computeDate: (d) => subtractWeeks(d, 8),
  },
  {
    key: 'participant_rampup',
    title: 'Participant Ramp-Up',
    offsetLabel: '4 weeks before',
    description:
      'Keep registered participants engaged so they show up and keep fundraising through the final stretch.',
    suggestions: [
      'Send training tips and a course preview email',
      'Share packet-pickup dates, parking, and gear-check info',
      'Recruit and schedule course and water-station volunteers',
      'Send a fundraising last-push reminder with leaderboards',
    ],
    icon: Footprints,
    computeDate: (d) => subtractWeeks(d, 4),
  },
  {
    key: 'race_week_logistics',
    title: 'Race Week Logistics',
    offsetLabel: '1 week before',
    description:
      'Finalize everything that has to be perfect at dawn. Walk the course and confirm every moving part.',
    suggestions: [
      'Run packet and bib pickup, including race-day pickup hours',
      'Confirm volunteer assignments and arrival times',
      'Set up the course, signage, timing mats, and barricades',
      'Confirm permits, road closures, and emergency services',
    ],
    icon: ClipboardList,
    computeDate: (d) => subtractWeeks(d, 1),
  },
  {
    key: 'race_day',
    title: 'Race Day',
    offsetLabel: 'Day of event',
    description:
      'The starting gun fires. Lead calmly, lean on your volunteers, and celebrate every finisher.',
    suggestions: [
      'Open check-in and day-of registration early',
      'Run start and finish-line operations and the announcer',
      'Post timing results and recognize top fundraisers',
      'Host the post-race celebration and thank everyone',
    ],
    icon: Medal,
    computeDate: (d) => new Date(d),
  },
]

const WEDDING_MILESTONES: Milestone[] = [
  {
    key: 'save_the_date',
    title: 'Save the Date',
    offsetLabel: '8 months before',
    description:
      'Give guests plenty of notice, especially if travel is involved. This is the first glimpse of your wedding style.',
    suggestions: [
      'Send save-the-dates to your full guest list',
      'Lock in the venue, date, and officiant',
      'Start and refine your guest list and addresses',
      'Reserve room blocks for out-of-town guests',
    ],
    icon: Heart,
    computeDate: (d) => subtractMonths(d, 8),
  },
  {
    key: 'invitations_sent',
    title: 'Invitations Sent',
    offsetLabel: '10 weeks before',
    description:
      'The formal invitations go out. Guests learn the schedule, the dress code, and exactly how to RSVP.',
    suggestions: [
      'Mail invitations with a clear RSVP deadline',
      'Open online RSVP and meal selection',
      'Share your wedding website with weekend details',
      'Confirm photographer, florist, music, and catering',
    ],
    icon: Mail,
    computeDate: (d) => subtractWeeks(d, 10),
  },
  {
    key: 'rsvp_details_wave',
    title: 'RSVP & Details Wave',
    offsetLabel: '6 weeks before',
    description:
      'Chase down replies and lock the details. The picture of your day starts coming into focus.',
    suggestions: [
      'Collect RSVPs and meal choices, and follow up on stragglers',
      'Confirm vendor timelines and final selections',
      'Share the weekend schedule and travel logistics',
      'Finalize attire fittings for the wedding party',
    ],
    icon: Sparkles,
    computeDate: (d) => subtractWeeks(d, 6),
  },
  {
    key: 'final_headcount',
    title: 'Final Headcount & Logistics',
    offsetLabel: '2 weeks before',
    description:
      'Turn replies into a plan. This is when the seating chart and vendor timeline come together.',
    suggestions: [
      'Give the final headcount to your caterer and venue',
      'Build the seating chart and place cards',
      'Share the day-of timeline with every vendor',
      'Make final payments and prepare vendor tip envelopes',
    ],
    icon: ClipboardList,
    computeDate: (d) => subtractWeeks(d, 2),
  },
  {
    key: 'wedding_week',
    title: 'Wedding Week',
    offsetLabel: '1 week before',
    description:
      'The final stretch. Tie up loose ends and hand off the details so you can be present.',
    suggestions: [
      'Hold the rehearsal and rehearsal dinner',
      'Assemble welcome bags for out-of-town guests',
      'Share a day-of contact sheet with the wedding party',
      'Confirm transportation and arrival times',
    ],
    icon: Gem,
    computeDate: (d) => subtractWeeks(d, 1),
  },
  {
    key: 'wedding_day',
    title: 'Wedding Day',
    offsetLabel: 'Day of event',
    description:
      'The day is finally here. Delegate to your coordinator, follow the timeline, and soak it all in.',
    suggestions: [
      'Follow the get-ready and first-look timeline',
      'Let your coordinator manage vendors and the schedule',
      'Keep an emergency kit and day-of essentials handy',
      'Slow down and be present in every moment',
    ],
    icon: PartyPopper,
    computeDate: (d) => new Date(d),
  },
]

const HAPPY_HOUR_MILESTONES: Milestone[] = [
  {
    key: 'set_and_announce',
    title: 'Set & Announce',
    offsetLabel: '3 weeks before',
    description:
      'Keep it light and quick. A happy hour comes together fast, so the goal is to pick a spot and get it on calendars.',
    suggestions: [
      'Pick a venue and confirm a date and time',
      'Post the invite across your channels and group chats',
      'Decide on the vibe: networking, mixer, or celebration',
      'Set a rough headcount target with the venue',
    ],
    icon: Rocket,
    computeDate: (d) => subtractWeeks(d, 3),
  },
  {
    key: 'rsvps_details',
    title: 'RSVPs & Details',
    offsetLabel: '2 weeks before',
    description:
      'Open RSVPs and share the essentials so guests know exactly where to be and what to expect.',
    suggestions: [
      'Open a simple RSVP link or guest list',
      'Share the location, time, and any drink specials',
      'Note whether it is open bar, cash bar, or first round covered',
      'Invite a few key guests personally to anchor the room',
    ],
    icon: Mail,
    computeDate: (d) => subtractWeeks(d, 2),
  },
  {
    key: 'promo_push',
    title: 'Promo Push',
    offsetLabel: '1 week before',
    description:
      'Build a little buzz. A timely reminder keeps the event top of mind and boosts your turnout.',
    suggestions: [
      'Post social reminders and a countdown',
      'Send personal nudges to guests who have not replied',
      'Confirm any guest speakers or special guests',
      'Reconfirm the reserved space with the venue',
    ],
    icon: Megaphone,
    computeDate: (d) => subtractWeeks(d, 1),
  },
  {
    key: 'final_reminder',
    title: 'Final Reminder',
    offsetLabel: '2 days before',
    description:
      'Lock in the count and send a last-call reminder so guests have the details fresh in hand.',
    suggestions: [
      'Confirm the final headcount with the venue',
      'Send a last-call reminder with the address and time',
      'Prepare name tags or a sign-in if it is a networking mixer',
      'Confirm the tab, payment, or sponsor coverage',
    ],
    icon: Bell,
    computeDate: (d) => subtractDays(d, 2),
  },
  {
    key: 'day_of',
    title: 'Day Of',
    offsetLabel: 'Day of event',
    description:
      'Show up, set the tone, and enjoy it. The hard part is done.',
    suggestions: [
      'Arrive early to greet the venue and set up',
      'Welcome guests and make warm introductions',
      'Capture a few photos for a recap post',
      'Thank guests and tease the next gathering',
    ],
    icon: Wine,
    computeDate: (d) => new Date(d),
  },
]

const MEAL_MILESTONES: Milestone[] = [
  {
    key: 'save_the_date_captains',
    title: 'Save the Date & Table Captains',
    offsetLabel: '3 months before',
    description:
      'Breakfasts and luncheons live and die by table captains. Recruit them early so they can fill their tables.',
    suggestions: [
      'Recruit table captains and set table-fill goals',
      'Confirm the venue, date, and start time',
      'Send save-the-dates to your core supporters',
      'Begin lining up the speaker or program theme',
    ],
    icon: Users,
    computeDate: (d) => subtractMonths(d, 3),
  },
  {
    key: 'invitations_program',
    title: 'Invitations & Program',
    offsetLabel: '6 weeks before',
    description:
      'Send the invitations and lock the program. Guests should know who they will hear from and why it matters.',
    suggestions: [
      'Send invitations and open registration',
      'Confirm the keynote speaker and program flow',
      'Give table captains their invite links and materials',
      'Publish the event page with the agenda and cause',
    ],
    icon: Mail,
    computeDate: (d) => subtractWeeks(d, 6),
  },
  {
    key: 'registration_push',
    title: 'Registration Push',
    offsetLabel: '3 weeks before',
    description:
      'Fill the remaining seats and gather the details that make the morning run smoothly.',
    suggestions: [
      'Follow up with captains to fill open seats',
      'Confirm RSVPs and collect dietary restrictions',
      'Gather AV needs and presentation materials',
      'Send a reminder to registered guests with logistics',
    ],
    icon: Megaphone,
    computeDate: (d) => subtractWeeks(d, 3),
  },
  {
    key: 'final_logistics',
    title: 'Final Logistics',
    offsetLabel: '1 week before',
    description:
      'Nail down every detail with the venue and run through the program one last time.',
    suggestions: [
      'Confirm the final headcount with the venue and caterer',
      'Finalize the seating chart, table assignments, and place cards',
      'Walk the run of show with speakers and the AV team',
      'Prepare a donation or pledge moment if fundraising',
    ],
    icon: ClipboardList,
    computeDate: (d) => subtractWeeks(d, 1),
  },
  {
    key: 'day_of',
    title: 'Day Of',
    offsetLabel: 'Day of event',
    description:
      'Mornings move fast. Arrive early, brief your team, and keep the program on time.',
    suggestions: [
      'Set up early and test the AV before doors open',
      'Brief volunteers, captains, and check-in staff',
      'Keep the program on schedule and on message',
      'Thank guests and captains within 24 hours',
    ],
    icon: Coffee,
    computeDate: (d) => new Date(d),
  },
]

const EVENT_TYPES: EventTypePlan[] = [
  { key: 'gala', label: 'Gala & Benefits', milestones: GALA_MILESTONES },
  { key: 'race', label: 'Races / Walks', milestones: RACE_MILESTONES },
  { key: 'wedding', label: 'Weddings', milestones: WEDDING_MILESTONES },
  { key: 'happy_hour', label: 'Happy Hours', milestones: HAPPY_HOUR_MILESTONES },
  { key: 'meal', label: 'Breakfasts & Luncheons', milestones: MEAL_MILESTONES },
]

const formatDate = (d: Date) =>
  d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })

const startOfDay = (d: Date) => {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

const daysBetween = (from: Date, to: Date) => {
  const ms = startOfDay(to).getTime() - startOfDay(from).getTime()
  return Math.round(ms / (1000 * 60 * 60 * 24))
}

const parseEventDate = (value: string): Date | null => {
  if (!value) return null
  const [yearStr, monthStr, dayStr] = value.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  const day = Number(dayStr)
  if (!year || !month || !day) return null
  const date = new Date(year, month - 1, day)
  return Number.isNaN(date.getTime()) ? null : date
}

const todayIsoDate = () => {
  const t = new Date()
  const y = t.getFullYear()
  const m = String(t.getMonth() + 1).padStart(2, '0')
  const d = String(t.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export default function EventAnnouncementPlannerShow() {
  const [eventTypeKey, setEventTypeKey] = useState<string | null>(null)
  const [eventDateInput, setEventDateInput] = useState('')
  const [completed, setCompleted] = useState<Record<string, boolean>>({})
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  const eventType = useMemo(
    () => EVENT_TYPES.find((t) => t.key === eventTypeKey) ?? null,
    [eventTypeKey]
  )

  const eventDate = useMemo(() => parseEventDate(eventDateInput), [eventDateInput])

  const today = useMemo(() => startOfDay(new Date()), [])

  const milestones = eventType?.milestones ?? []

  const milestoneRows = useMemo(() => {
    if (!eventType || !eventDate) return []
    return eventType.milestones.map((m) => {
      const date = startOfDay(m.computeDate(eventDate))
      const days = daysBetween(today, date)
      const status: 'past' | 'today' | 'upcoming' =
        days < 0 ? 'past' : days === 0 ? 'today' : 'upcoming'
      return { ...m, date, days, status }
    })
  }, [eventType, eventDate, today])

  const completedCount = milestoneRows.filter((m) => completed[m.key]).length

  const selectEventType = (key: string) => {
    setEventTypeKey(key)
    // A "done"/expanded flag keyed to another type's milestone shouldn't carry over.
    setCompleted({})
    setExpanded({})
  }

  const toggleCompleted = (key: string) =>
    setCompleted((prev) => ({ ...prev, [key]: !prev[key] }))

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))

  const expandAll = () => {
    const next: Record<string, boolean> = {}
    for (const m of milestones) next[m.key] = true
    setExpanded(next)
  }

  const collapseAll = () => setExpanded({})

  const reset = () => {
    setCompleted({})
    setExpanded({})
  }

  const downloadPdf = () => {
    expandAll()
    requestAnimationFrame(() => {
      requestAnimationFrame(() => window.print())
    })
  }

  const ready = !!eventType && !!eventDate

  return (
    <>
      <Head title="Event Announcement Planner">
        <meta
          head-key="description"
          name="description"
          content="Plan every announcement, invitation, and engagement push leading up to your event with an interactive, event-type-specific timeline."
        />
      </Head>

      {/* Hero with type + date input */}
      <div className="relative overflow-hidden bg-linear-to-br from-[oklch(0.25_0.02_50)] via-[oklch(0.35_0.10_46)] to-primary">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(255,255,255,0.12),transparent)]" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
          <h1 className="max-w-[24ch] text-4xl font-semibold tracking-tight text-white text-balance sm:text-5xl lg:text-6xl">
            Event Announcement Planner
          </h1>
          <p className="mt-4 max-w-[60ch] text-lg font-medium text-white/85 text-pretty">
            Choose your event type and date, and we will build an interactive timeline of every save-the-date, invitation, and engagement push leading up to the big day.
          </p>

          <div className="mt-8 print:hidden">
            <Label className="text-sm font-medium text-white/90">Event type</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {EVENT_TYPES.map((type) => {
                const isSelected = type.key === eventTypeKey
                return (
                  <Button
                    key={type.key}
                    type="button"
                    variant="outline"
                    onClick={() => selectEventType(type.key)}
                    aria-pressed={isSelected}
                    className={
                      isSelected
                        ? 'border-white bg-white font-semibold text-foreground hover:bg-white hover:text-foreground'
                        : 'border-white/30 bg-white/10 font-semibold text-white backdrop-blur-sm hover:bg-white/20 hover:text-white'
                    }
                  >
                    {type.label}
                  </Button>
                )
              })}
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 print:hidden sm:flex-row sm:items-end">
            <div className="flex-1 sm:max-w-xs">
              <Label htmlFor="event_date" className="text-sm font-medium text-white/90">
                Event date
              </Label>
              <Input
                id="event_date"
                type="date"
                min={todayIsoDate()}
                value={eventDateInput}
                onChange={(e) => setEventDateInput(e.target.value)}
                className="mt-2 h-10 bg-white text-foreground ring-1 ring-white/20 focus-visible:ring-2 focus-visible:ring-white"
              />
            </div>
            {ready && (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  className="border-white/30 bg-white/10 font-semibold text-white backdrop-blur-sm hover:bg-white/20 hover:text-white"
                  onClick={expandAll}
                >
                  Expand all
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  className="border-white/30 bg-white/10 font-semibold text-white backdrop-blur-sm hover:bg-white/20 hover:text-white"
                  onClick={collapseAll}
                >
                  Collapse all
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        {!ready ? (
          <EmptyState needsType={!eventType} />
        ) : (
          <>
            <div className="mb-10 flex flex-col gap-3 rounded-2xl bg-muted/50 p-5 ring-1 ring-black/5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {eventType.label} <span aria-hidden="true">·</span> Event day
                </p>
                <p className="text-xl font-semibold tracking-tight">{formatDate(eventDate)}</p>
              </div>
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Progress</p>
                  <p className="text-xl font-semibold tracking-tight">
                    {completedCount} / {milestones.length}
                  </p>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={reset} className="print:hidden">
                  Reset
                </Button>
              </div>
            </div>

            <Timeline
              rows={milestoneRows}
              completed={completed}
              expanded={expanded}
              onToggleCompleted={toggleCompleted}
              onToggleExpanded={toggleExpanded}
            />

            <div className="mt-10 flex justify-center print:hidden">
              <Button type="button" size="lg" onClick={downloadPdf}>
                <Download className="size-4" />
                Download PDF
              </Button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @media print {
          @page { margin: 0.5in; }
          nav, footer { display: none !important; }
          body { background: white !important; }
        }
      `}</style>
    </>
  )
}

function EmptyState({ needsType }: { needsType: boolean }) {
  return (
    <div className="rounded-2xl bg-muted/50 p-10 text-center ring-1 ring-black/5">
      <CalendarHeart className="mx-auto size-10 text-primary/60" aria-hidden="true" />
      <h2 className="mt-4 text-xl font-semibold tracking-tight">
        {needsType ? 'Choose an event type to begin' : 'Pick your event date'}
      </h2>
      <p className="mt-2 text-base text-muted-foreground">
        {needsType
          ? 'Select the kind of event you are planning, then choose a date and we will lay out every milestone on an interactive timeline.'
          : 'Once you choose your event date, we will lay out every milestone on an interactive timeline.'}
      </p>
    </div>
  )
}

type Row = Milestone & {
  date: Date
  days: number
  status: 'past' | 'today' | 'upcoming'
}

type TimelineProps = {
  rows: Row[]
  completed: Record<string, boolean>
  expanded: Record<string, boolean>
  onToggleCompleted: (key: string) => void
  onToggleExpanded: (key: string) => void
}

function Timeline({ rows, completed, expanded, onToggleCompleted, onToggleExpanded }: TimelineProps) {
  return (
    <ol className="relative ml-3 border-l-2 border-dashed border-primary/30">
      {rows.map((row, idx) => (
        <TimelineItem
          key={row.key}
          row={row}
          isLast={idx === rows.length - 1}
          isCompleted={!!completed[row.key]}
          isExpanded={!!expanded[row.key]}
          onToggleCompleted={() => onToggleCompleted(row.key)}
          onToggleExpanded={() => onToggleExpanded(row.key)}
        />
      ))}
    </ol>
  )
}

type TimelineItemProps = {
  row: Row
  isLast: boolean
  isCompleted: boolean
  isExpanded: boolean
  onToggleCompleted: () => void
  onToggleExpanded: () => void
}

function TimelineItem({
  row,
  isLast,
  isCompleted,
  isExpanded,
  onToggleCompleted,
  onToggleExpanded,
}: TimelineItemProps) {
  const Icon = row.icon

  const dotClasses =
    row.status === 'today'
      ? 'bg-secondary text-foreground ring-secondary/40'
      : row.status === 'past'
        ? 'bg-primary/20 text-primary ring-primary/20'
        : 'bg-primary text-primary-foreground ring-primary/30'

  const statusLabel =
    row.status === 'today'
      ? 'Today'
      : row.status === 'past'
        ? `${Math.abs(row.days)} ${Math.abs(row.days) === 1 ? 'day' : 'days'} ago`
        : `In ${row.days} ${row.days === 1 ? 'day' : 'days'}`

  return (
    <li className={`relative pl-8 ${isLast ? 'pb-0' : 'pb-8'}`}>
      <span
        className={`absolute -left-[17px] top-1 flex size-8 items-center justify-center rounded-full ring-4 ring-background ${dotClasses}`}
        aria-hidden="true"
      >
        <Icon className="size-4" />
      </span>

      <button
        type="button"
        onClick={onToggleExpanded}
        className={`group block w-full rounded-2xl bg-card p-5 text-left shadow-sm ring-1 transition-all hover:shadow-lg ${
          isCompleted ? 'ring-primary/40 bg-primary/5' : 'ring-black/5 hover:ring-primary/20'
        }`}
        aria-expanded={isExpanded}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3
                className={`text-lg font-semibold tracking-tight ${
                  isCompleted ? 'text-muted-foreground line-through decoration-2' : ''
                }`}
              >
                {row.title}
              </h3>
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                {row.offsetLabel}
              </span>
              {row.status === 'today' && (
                <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-foreground">
                  Today
                </span>
              )}
            </div>
            <p className="mt-1 text-sm font-medium text-muted-foreground">
              {formatDate(row.date)} <span aria-hidden="true">·</span> {statusLabel}
            </p>
          </div>

          <div className="flex items-center gap-2 print:hidden">
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1 text-sm font-medium text-muted-foreground hover:bg-muted"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={isCompleted}
                onChange={onToggleCompleted}
                className="size-4 cursor-pointer accent-primary"
              />
              Done
            </label>
            <span
              className="rounded-lg p-1 text-muted-foreground transition-transform group-hover:text-foreground"
              aria-hidden="true"
            >
              {isExpanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
            </span>
          </div>
        </div>

        {isExpanded && (
          <div className="mt-4 border-t border-black/5 pt-4">
            <p className="text-base text-foreground">{row.description}</p>
            <ul className="mt-4 space-y-2">
              {row.suggestions.map((s) => (
                <li key={s} className="flex items-start gap-2 text-sm text-foreground">
                  <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </button>
    </li>
  )
}
